import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  area: {
    height: 300,
    overflowY: "auto",
    padding: 10,
    ...theme.scrollbarStyles,
  },
  grade: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(78px, 1fr))",
    gap: 8,
  },
  item: {
    position: "relative",
    aspectRatio: "1 / 1",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 4,
    transition: "background-color 0.15s",

    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "#f0f2f5",
    },
    "&:hover $remover": { opacity: 1 },
  },
  figurinha: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    pointerEvents: "none",
  },
  remover: {
    position: "absolute",
    top: -4,
    right: -4,
    opacity: 0,
    transition: "opacity 0.15s",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
    padding: 3,
    "&:hover": { backgroundColor: theme.palette.background.paper },
  },
  criar: {
    aspectRatio: "1 / 1",
    borderRadius: 8,
    border: `1px dashed ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: theme.palette.text.secondary,
    fontSize: "0.68rem",
    gap: 2,
    "&:hover": { borderColor: "#1E9855", color: "#1E9855" },
  },
  vazio: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    padding: "40px 20px",
    lineHeight: 1.6,
  },
  centro: {
    display: "flex",
    justifyContent: "center",
    padding: 40,
  },
}));

const SeletorFigurinhas = ({ ticketId, aberto, onEnviado }) => {
  const classes = useStyles();

  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(null);
  const inputRef = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/stickers");
      setLista(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  const enviar = async (sticker) => {
    if (!ticketId || enviando) return;

    setEnviando(sticker.id);
    try {
      await api.post(`/stickers/${sticker.id}/send/${ticketId}`);
      if (onEnviado) onEnviado();
    } catch (err) {
      toastError(err);
    } finally {
      setEnviando(null);
    }
  };

  const criar = async (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = "";
    if (!arquivo) return;

    if (!arquivo.type.startsWith("image/")) {
      toast.error("Escolha uma imagem.");
      return;
    }

    setCarregando(true);
    try {
      const corpo = new FormData();
      corpo.append("sticker", arquivo);

      await api.post("/stickers", corpo, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Figurinha criada!");
      await carregar();
    } catch (err) {
      toastError(err);
      setCarregando(false);
    }
  };

  const remover = async (e, sticker) => {
    e.stopPropagation();
    try {
      await api.delete(`/stickers/${sticker.id}`);
      setLista((atual) => atual.filter((s) => s.id !== sticker.id));
    } catch (err) {
      toastError(err);
    }
  };

  if (carregando) {
    return (
      <div className={classes.area}>
        <div className={classes.centro}>
          <CircularProgress size={28} style={{ color: "#1E9855" }} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.area}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={criar}
      />

      {!lista.length ? (
        <div className={classes.vazio}>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            Nenhuma figurinha salva ainda.
          </Typography>
          <div
            className={classes.criar}
            style={{ width: 90, height: 90, margin: "0 auto" }}
            onClick={() => inputRef.current?.click()}
          >
            <AddIcon />
            Criar
          </div>
          <Typography variant="caption" style={{ display: "block", marginTop: 12 }}>
            Você também pode salvar figurinhas que os clientes enviarem,
            pelo menu da mensagem.
          </Typography>
        </div>
      ) : (
        <div className={classes.grade}>
          <div className={classes.criar} onClick={() => inputRef.current?.click()}>
            <AddIcon />
            Criar
          </div>

          {lista.map((s) => (
            <div
              key={s.id}
              className={classes.item}
              onClick={() => enviar(s)}
              title="Enviar figurinha"
            >
              {enviando === s.id ? (
                <CircularProgress size={22} style={{ color: "#1E9855" }} />
              ) : (
                <img
                  className={classes.figurinha}
                  src={`${getBackendUrl()}public/${s.filename}`}
                  alt="figurinha"
                />
              )}

              <Tooltip title="Remover da lista">
                <IconButton
                  size="small"
                  className={classes.remover}
                  onClick={(e) => remover(e, s)}
                >
                  <DeleteOutlineIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SeletorFigurinhas;
